import { useCallback, useEffect, useRef } from 'react';
import { readUtmParams } from '../utmParams';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const INACTIVITY_MS = 2 * 60 * 1000; // 2 minutes

function extractPayload(form, page, donationType, amount) {
  return {
    firstName: form.firstName || '',
    lastName: form.lastName || '',
    address: form.address || '',
    city: form.city || '',
    phone: form.phone || '',
    email: form.email || '',
    birthYear: form.birthYear || '',
    page,
    donationType: donationType || '',
    amount: amount || '',
    ...readUtmParams(),
  };
}

function formHasData(form) {
  return Object.values(form).some((v) => v && typeof v === 'string' && v.trim() !== '');
}

// Tracks partial form submissions for abandoned-form capture.
//
// form         - controlled form state (must include firstName/lastName/city/phone/email)
// page         - label for the "אתר" sheet column (e.g. 'הצטרפות', 'תרומה')
// donationType - label for the "סוג תרומה" column (e.g. 'חודשי', 'חד פעמי')
// amount       - chosen donation amount, for the "סכום תרומה" column
// submitted    - set to true once payment completes; suppresses all sends
//
// Returns { sendNow } — call it when the user navigates away within the SPA
// (e.g. internal back button) so the data is captured without a page unload.
//
// Triggers:
//  1. sendNow()       — internal SPA navigation (back button)
//  2. beforeunload    — tab close / browser navigate away / refresh
//  3. 2-min idle      — no field change AND no `activity` change for INACTIVITY_MS
//
// activity - any value that should also reset the idle clock even though `form`
// itself stops changing, e.g. `igSubmitted` — once the donor moves into the
// hosted card iframe, they're actively checking out (not idle), and card entry
// + 3-D Secure can easily take longer than INACTIVITY_MS on its own.
export function useAbandonedForm({ form, page, donationType, amount, submitted, activity }) {
  const latest = useRef({ form, page, donationType, amount, submitted });
  const lastSentRef = useRef(null); // JSON of last queued payload — prevents duplicates

  useEffect(() => {
    latest.current = { form, page, donationType, amount, submitted };
  });

  // Core send: shared by all three triggers.
  // useBeacon=true uses sendBeacon (required during page unload).
  const trySend = useCallback((useBeacon = false) => {
    const { form, page, donationType, amount, submitted } = latest.current;
    if (submitted) return;
    if (!formHasData(form)) return;

    const payload = extractPayload(form, page, donationType, amount);
    const payloadStr = JSON.stringify(payload);
    if (lastSentRef.current === payloadStr) return; // nothing new since last send
    lastSentRef.current = payloadStr;

    if (useBeacon) {
      navigator.sendBeacon(
        `${API_BASE}/api/partial-form`,
        new Blob([payloadStr], { type: 'application/json' })
      );
    } else {
      fetch(`${API_BASE}/api/partial-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payloadStr,
      }).catch(() => {});
    }
  }, []);

  // Trigger 3: 2-minute inactivity timer.
  // Resets every time `form` or `amount` gets a new value (i.e. any field/amount change).
  useEffect(() => {
    const timer = setTimeout(() => trySend(), INACTIVITY_MS);
    return () => clearTimeout(timer);
  }, [form, amount, activity, trySend]);

  // Trigger 2: tab close / navigate away / refresh.
  useEffect(() => {
    const onBeforeUnload = () => trySend(true);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [trySend]);

  // Trigger 1: exposed for SPA internal navigation (back button etc.).
  const sendNow = useCallback(() => trySend(), [trySend]);

  return { sendNow };
}
